/**
 * The navigation layer: what the AI cats path with.
 *
 * Two layers, and the split is the point.  The FINE layer is the same 0.1 m
 * standing-height field reach.mjs floods -- the one resolution at which the
 * level's walls, kerbs and stair treads all resolve -- but half a million
 * cells is a map you can flood once at boot, not a graph you can A* sixteen
 * times a tick.  So a COARSE waypoint graph is built over it: nodes every
 * couple of metres of reachable ground (one per height plateau, see
 * _placeNodes) plus one forced at every place anyone will ask for, with an
 * edge only where the fine grid proves the move between them.  The coarse
 * graph answers "which way round the house"; the fine grid stays the
 * authority on "can this actual metre be crossed", and every edge and every
 * string-pull shortcut is checked against it.
 *
 * The sampling primitives (standFn, HeightField, jumpMoves, blockedLine,
 * floodJump) are exported because reach.mjs floods with them too: reach.mjs
 * is the deployment gate for "is the level connected", and the moment it and
 * the AI's graph model different worlds, one of them is lying.
 */
import { MOVE, BOUNDS, SPAWN } from "./spec.js";
import { ColliderGrid } from "../collision.js";
import { surfaceLookup } from "./index.js";
import { approachPoint } from "@catworld/shared";

/** Headroom a cat needs to stand somewhere. */
export const CLEARANCE = 0.34;
/** Ignore surfaces above this (roofs, canopies). */
export const MAX_STAND = 6.0;
/** Horizontal metres a standing jump can cross (see jumpMoves). */
export const JUMP_REACH = 1.15;

/**
 * Height of the surface the cat would stand on at (x, z), or null.
 *
 * Candidate floors are the surface itself -- the ground, or the paving and
 * sod laid on it, which is what the player actually floors against -- plus
 * the top of anything underfoot, highest first, and the first one with
 * CLEARANCE of open air above it wins.  One height per column, on purpose:
 * it keeps the field a Float32Array, and the cost is that anything under a
 * roof samples the roof, and a walk-UNDER (the burrow's lifted chain-link,
 * the missing fence board) samples the thing walked under.  reach.mjs says
 * the same; walk.mjs answers those routes with the real player.
 */
export function standFn(grid, surfaceY) {
  return function standHeight(x, z) {
    const spans = [];
    for (const c of grid.nearPoint(x, z)) {
      if (x < c.min.x || x > c.max.x || z < c.min.z || z > c.max.z) continue;
      spans.push(c);
    }
    const tops = [surfaceY(x, z)];
    for (const c of spans) if (c.max.y <= MAX_STAND) tops.push(c.max.y);
    tops.sort((p, q) => q - p);                  // highest first
    for (const h of tops) {
      let clear = true;
      for (const c of spans) {
        // something occupying the space just above this surface
        if (c.max.y > h + 1e-3 && c.min.y < h + CLEARANCE) { clear = false; break; }
      }
      if (clear) return h;
    }
    return null;
  };
}

/**
 * The fine grid: one standing height per step-sized cell across the whole
 * level, NaN where there is nothing to stand on.
 *
 * The step has to stay finer than the thinnest BARRIER collider in the level
 * (the fence panels are 0.12 m; the front pickets 0.10) or a wall can sit
 * entirely between two sample columns and everything built on this field --
 * reach's flood, the edge lines, the whole graph -- walks straight through
 * it.  Nav.build refuses to build rather than hand back a confident, wrong
 * graph; reach.mjs makes the same check and prints it.
 */
export class HeightField {
  constructor({ step, nx, nz, minX, minZ, height }) {
    this.step = step;
    this.nx = nx;
    this.nz = nz;
    this.minX = minX;
    this.minZ = minZ;
    this.height = height;
  }

  static sample({ grid, surfaceY, step = 0.1, bounds = BOUNDS }) {
    const nx = Math.floor((bounds.maxX - bounds.minX) / step);
    const nz = Math.floor((bounds.maxZ - bounds.minZ) / step);
    const height = new Float32Array(nx * nz).fill(NaN);
    const stand = standFn(grid, surfaceY);
    for (let i = 0; i < nx; i++) {
      const x = bounds.minX + i * step;
      for (let j = 0; j < nz; j++) {
        const h = stand(x, bounds.minZ + j * step);
        if (h !== null) height[i * nz + j] = h;
      }
    }
    return new HeightField({ step, nx, nz, minX: bounds.minX,
                             minZ: bounds.minZ, height });
  }

  idx(i, j) { return i * this.nz + j; }
  wx(i) { return this.minX + i * this.step; }
  wz(j) { return this.minZ + j * this.step; }
  /** Column indices of a world point -- reach.mjs rounds the same way. */
  ci(x) { return Math.round((x - this.minX) / this.step); }
  cj(z) { return Math.round((z - this.minZ) / this.step); }
  inside(i, j) { return i >= 0 && j >= 0 && i < this.nx && j < this.nz; }
}

/**
 * Neighbours a cat could reach in one move, as [di, dj, metres] offsets.
 *
 * Adjacent cells cover walking; the wider ring covers *jumping a gap* -- a
 * bin lid 0.26 m from a crate is a trivial hop for a cat but shares no edge
 * with it on the sample grid, and connecting only touching cells reports
 * such routes as broken.  Short range takes every offset, so walking and
 * stepping are exact; long range takes rings sampled by direction, because a
 * full disc at this resolution is tens of millions of edges for no extra
 * fidelity.
 */
export function jumpMoves(step) {
  const NB = [];
  const close = Math.ceil(0.25 / step);
  for (let di = -close; di <= close; di++) {
    for (let dj = -close; dj <= close; dj++) {
      if (!di && !dj) continue;
      NB.push([di, dj, Math.hypot(di, dj) * step]);
    }
  }
  for (let r = close + 2; r * step <= JUMP_REACH; r += 2) {
    for (let a = 0; a < 24; a++) {
      const th = (a / 24) * Math.PI * 2;
      const di = Math.round(Math.cos(th) * r), dj = Math.round(Math.sin(th) * r);
      if (!NB.some(([p, q]) => p === di && q === dj)) {
        NB.push([di, dj, Math.hypot(di, dj) * step]);
      }
    }
  }
  NB.sort((a, b) => a[2] - b[2]);
  return NB;
}

/** Would something in between block a hop from (i,j) to (ni,nj)? */
export function blockedLine(f, i, j, ni, nj, h, nh) {
  const steps = Math.max(Math.abs(ni - i), Math.abs(nj - j));
  if (steps < 2) return false;
  const ceiling = Math.max(h, nh) + 0.25;
  for (let s = 1; s < steps; s++) {
    const t = s / steps;
    const mi = Math.round(i + (ni - i) * t);
    const mj = Math.round(j + (nj - j) * t);
    const mh = f.height[f.idx(mi, mj)];
    if (!Number.isNaN(mh) && mh > ceiling) return true;  // a wall in the way
  }
  return false;
}

/**
 * Flood out from a seed cell with the cat's full movement: stepping up to
 * MOVE.stepHeight is free, up to jumpHeight costs a jump, and dropping any
 * survivable distance is always allowed.  Reaching further costs height --
 * a standing jump goes up OR across, not both at full value.
 *
 * This is reach.mjs's flood, verbatim, and it must stay that way: the set it
 * returns is the level's definition of "reachable", and two definitions is
 * how a gate and a game come to disagree.
 */
export function floodJump(f, si, sj, moves = jumpMoves(f.step)) {
  const { height, nx, nz } = f;
  const seen = new Uint8Array(nx * nz);
  const queue = [f.idx(si, sj)];
  seen[queue[0]] = 1;
  while (queue.length) {
    const cur = queue.pop();
    const i = (cur / nz) | 0, j = cur % nz;
    const h = height[cur];
    for (const [di, dj, d] of moves) {
      const ni = i + di, nj = j + dj;
      if (ni < 0 || nj < 0 || ni >= nx || nj >= nz) continue;
      const k = f.idx(ni, nj);
      if (seen[k]) continue;
      const nh = height[k];
      if (Number.isNaN(nh)) continue;
      const rise = nh - h;
      const budget = MOVE.jumpHeight * (1 - 0.45 * (d / JUMP_REACH));
      if (rise > (d <= f.step * 1.5 ? MOVE.stepHeight + 1e-6 : budget)) continue;
      if (rise < -4) continue;                             // a lethal drop
      if (blockedLine(f, i, j, ni, nj, h, nh)) continue;
      seen[k] = 1;
      queue.push(k);
    }
  }
  return seen;
}

// ---------------------------------------------------------------------------
// The movement rules the graph is built from.

/**
 * THE most load-bearing decision in this file: an edge is either a WALK or a
 * single HOP, proved cell by cell on the fine grid, and nothing else.
 *
 * A walk is what plain held direction keys execute: each 0.1 m fine step may
 * rise at most MOVE.stepHeight and fall at most WALK_DROP, every sample
 * standable.  A hop is a walk with exactly ONE jump in it -- somewhere along
 * the line the height breaks the walking rules once, and that break must fit
 * reach.mjs's own budget (an uncharged jump goes up OR across, not both at
 * full value, and the blocked-line check still applies).  The AI's steering
 * drives with WASD and can press Space once per hop waypoint; what it cannot
 * do is chain mid-air decisions, cling, or charge a pounce -- so two breaks
 * on one line is two jumps, and the edge is refused.  Chains of hops are
 * still routable, because every landing between them (a recycle-box top, a
 * bin lid, the chain-link's top rail) carries a node of its own (see
 * _placeNodes) and each link is one edge with one hop.
 *
 * The first cut of this graph allowed walking only, on the theory that a
 * brain-driven cat should not path over jumps at all.  Measured against the
 * shipped level that theory is untenable: a pure walking flood from the
 * spawn reaches 300203 of 505665 standable cells and serves exactly ONE of
 * the six solo happy places (sunspot), because the level was designed for a
 * cat that hops -- the grade change behind the houses, the steps at the bin
 * lane, the ajar gate with the recycle stack parked in it and the burrow's
 * rail are all rises past 0.23 m.  Raising the fall limit alone (even to an
 * unlimited 4 m) only adds lawn and deck; creek, shed and binlane stay
 * sealed.  A cat AI that cannot hop a 0.3 m step is not a cat.
 *
 * Deliberately NOT excluded: places the design reserves for a human that
 * the movement rules genuinely reach.  With hops in the graph the nav can
 * still get to spots `solo: false` in shared/happy.js marks as needing
 * hands (the car bonnet, the butterflies).  That column is the design
 * authority for what the brain will TARGET; the nav's only duty is to
 * serve the solo rows, and pruning the rest out of the graph here would
 * just make the next elevated marker's route report a mystery.  It works
 * out better than a filter would anyway: with the narrow-top rule in
 * _placeNodes, fencewalk, deckrail and shedroof come out with NO ROUTE
 * because the geometry itself demands a skill the brain lacks -- balance,
 * or a charged pounce -- which is the same design fact enforced one layer
 * deeper than the flag.
 *
 * WALK_DROP is 0.60 m.  Falling is the one vertical move held keys execute
 * on their own -- walk off the lip and gravity does the rest -- so the
 * level's real ledges walk in both directions (the 0.13 kerb, the 0.17
 * porch risers, the 0.22 deck risers) or at least downward (the 0.42
 * recycle boxes, the 0.44 planters).  Past 0.60 the fall still routes, but
 * as a hop -- the first such ledge is the deck edge at 1.35 m to the patio
 * -- which prices it against taking the stair and tells the steering a
 * plain hold-forward will leave the ground.
 */
const WALK_RISE = MOVE.stepHeight + 1e-6;
const WALK_DROP = 0.60;
/** The biggest drop a hop may take -- reach.mjs's "lethal drop" line. */
const HOP_DROP = 4;

/**
 * The tallest rise a hop edge may claim: NOT the player's ideal 0.95 m
 * apex, but what the server's cat measurably mounts.  The AI's world runs
 * on 50 ms ticks with the physics sub-stepped three times, and a tap jump
 * on that rig measures 0.930 m of apex (semi-implicit integration eats
 * V0 * dt / 2 of the continuous arc; before the sub-stepping it was only
 * 0.814, and rises past 0.80 were unmountable).  steer.js clamps its own
 * press maths at 0.90 for the same reason, and the graph must not promise
 * what the steering refuses to attempt: an edge past this line is not
 * ambitious, it is a cat pressing Space at a wall for as long as the
 * server runs.  In practice the flood's own distance-scaled budget is the
 * tighter bound everywhere but point-blank range (it passes 0.90 only for
 * hops under ~0.13 m of reach).
 */
const HOP_RISE = 0.90 + 1e-6;

/**
 * The cat's standing height -- player.js HEIGHT, the capsule the physics
 * actually moves.  A hop take-off needs this much body plus the whole
 * rise to fit under whatever hangs over the take-off cell; see the
 * headroom check in _lineKind.
 */
const CAT_HEIGHT = 0.32;

/**
 * Narrow tops are landable, not walkable.
 *
 * The distinction is written into the game itself: player.js's landing
 * assist exists to make narrow tops CATCHABLE from the air (ASSIST_TOP =
 * 0.20 -- fence runs are 0.16 m, gate posts 0.13, and the thinnest thing
 * meant to be simply walked on, a street tree's trunk collider, is 0.27),
 * while walking a 0.16 m cap under held keys is the fencewalk challenge,
 * one of the skills the design reserves for a person.  The first live soak
 * proved what happens when the graph confuses the two: a route read "hop
 * onto the side fence's cap, walk 5.00 m along it, hop down", and seven of
 * eight cats spent five minutes walking off the side, re-pathing, climbing
 * back and falling again -- 155 m of travel, no arrivals.
 *
 * So narrowness gates WALKING, never existence: a cell whose surface
 * cannot be proven wider than ASSIST_TOP in both plan axes (three
 * contiguous 0.1 m columns within stepHeight -- an extent over 0.2 m)
 * refuses walk steps entirely.  A hop may still land on it and the next
 * hop may leave it, which is exactly how a cat crosses a fence -- and how
 * the level MEANS the back gardens to be entered: the side gates are shut,
 * and yards.js builds and marks the route-bins climb (ground, recycle box,
 * bin lid, fence cap, garden) on purpose.  In _lineKind terms: a walk step
 * touching a narrow cell is a break, so an edge may only pass a narrow
 * cell as a hop's landing at the edge's very end, or leave one as the
 * take-off at its very start -- "if a hop's landing is narrow, the next
 * leg must be another hop" falls out for free.
 */
const NARROW_SPAN = 3;

/**
 * What a hop costs, as a multiplier on its length.
 *
 * Chosen by rebuilding the graph and re-routing the spawn to all eleven
 * happy places at each candidate.  On the finished rules, 1x carries 57
 * jumps across the ten routes that resolve -- the router hops anything in
 * reach because a hop and a walk of the same metre cost the same, and the
 * bin-lane route vaults the parked car's bonnet rather than walk the 5 m
 * round it.  At 2.5x the elective hops are gone (36 jumps, bonnet
 * included: its wheel-arch landing is narrow, so NARROW_LAND below
 * multiplies in) and what remains is the level's own hop chains -- bins,
 * gateway, rear crossing.  6x changes only marginal trades (the deck route
 * pays 3.8 m of extra walking to shed one jump), so past 2.5 there is
 * nothing left to buy.  2.5 also has a physical reading: a jump genuinely
 * costs time, ~0.7 s airborne against 1.6 s to walk the metre, plus the
 * lining-up the steering does either side of it.
 */
const HOP_COST = 2.5;

/**
 * The extra multiplier for a hop whose LANDING is a narrow top.
 *
 * Landing on a cap is legal -- it is how a cat crosses a fence, and the
 * landing assist exists for it -- but live it is the least reliable move
 * the steering owns, and the probe run showed exactly how it fails: a cat
 * on the bin lid aiming the 0.65 m hop at the 0.16 m cap clips the fence
 * face, falls into the gate pocket, and tries again for ever, while a
 * cap-free chain into the same garden (bin lid, gateway recycle stack,
 * drop off its far side -- every landing a real floor) sits one lot to
 * the east.  4x is enough that A* takes the floor-landing chain anywhere
 * one exists and keeps the narrow landing only where it is the route --
 * the rear rail for a cat asked to stand exactly there (fencewalk's own
 * approach) -- while also pricing the car's wheel arch out of the
 * bin-lane commute (see HOP_COST above).
 */
const NARROW_LAND = 4;

/**
 * How far apart the coarse nodes sit, and how far the graph will look for
 * an edge partner: Chebyshev 2 lattice cells, i.e. lines up to ~5.7 m.  Two
 * cells rather than one so a node can see past a neighbouring cell whose
 * own node landed on the wrong side of a fence, which is what keeps the
 * graph connected along boundaries without any special casing.
 */
const EDGE_REACH = 2;

/*
 * There is deliberately NO hug penalty (BreachProtocol's HUG_COST) on walk
 * edges, and the reason is a measurement, taken twice.  On an early cut of
 * this graph -- before the narrow-top rules and the interior node
 * placement -- pricing wall-hugging lines at 0.45/m was clearly worth it:
 * across twenty spawn-to-elsewhere test legs it halved the number grazing
 * within one cell of a face and cut the bin lane's tight running from 45%
 * of its length to 10%, for 0.7% of extra distance.  Re-measured on the
 * finished rules, the same twenty legs come out IDENTICAL with the penalty
 * on or off (482.6 m against 482.5 m, same eleven legs pinching below
 * 0.1 m -- all of them genuine squeezes like the bin lane itself): the
 * narrow-walk rule and the interior-cell node placement now keep lines off
 * walls wholesale, and the penalty had nothing left to price.  A knob that
 * measurably does nothing is a lie waiting to be believed, so it is gone;
 * if a future module reintroduces long wall-side diagonals, measure first
 * and bring it back second.
 */

/** Search radius when snapping a path endpoint to the graph, metres. */
const SNAP_DIST = 2.5;

// ---------------------------------------------------------------------------

export class Nav {
  constructor(priv) { Object.assign(this, priv); }

  /**
   * Build the whole layer.  Synchronous work behind an async face, so the
   * server can await it next to everything else it boots.
   */
  static async build({ colliders, surfaces, surfaceY,
                       step = 0.1, spacing = 2.0, anchors = [] } = {}) {
    const t0 = performance.now();
    const grid = new ColliderGrid(colliders, 4);
    surfaceY ??= surfaceLookup(surfaces);

    // The step-vs-barrier guard, carried over from reach.mjs: a collider
    // thinner than the sample spacing can fall entirely between two columns,
    // and the graph then routes cats through a wall.  Only barrier-shaped
    // boxes matter -- thin one way, long the other, running from the ground
    // up past jump height -- a 0.06 m washing-line post is thinner than the
    // sampling too, but missing it cannot seal anything off.  reach.mjs
    // prints this and carries on; a library building a graph the server will
    // trust has no one reading its warnings, so it refuses instead.
    const barriers = colliders.filter((c) => {
      const w = c.max.x - c.min.x, d = c.max.z - c.min.z;
      const thin = Math.min(w, d), long = Math.max(w, d);
      return long >= 1 && thin < long / 4 &&
             c.min.y < 0.3 && c.max.y > MOVE.jumpHeight;
    });
    const thinnest = barriers.length
      ? Math.min(...barriers.map((c) => Math.min(c.max.x - c.min.x, c.max.z - c.min.z)))
      : Infinity;
    if (thinnest < step) {
      throw new Error(`nav: thinnest barrier is ${thinnest.toFixed(3)} m but ` +
        `sampling at ${step} m -- a wall this thin can fall between samples ` +
        `and the graph would route cats through it`);
    }

    const field = HeightField.sample({ grid, surfaceY, step });
    let standable = 0;
    for (const h of field.height) if (!Number.isNaN(h)) standable++;

    // The reachable set, with the cat's FULL movement (reach.mjs's rules).
    // Slightly wider than what the graph connects -- the flood may chain
    // jumps the graph has no landing node for -- but every node and every
    // endpoint is required to sit inside it, which keeps this layer's idea
    // of "reachable" identical to the one the deployment gate reports on.
    const seen = floodJump(field, field.ci(SPAWN.x), field.cj(SPAWN.z));
    let reached = 0;
    for (const v of seen) reached += v;

    // Label the reachable cells into connected FLOORS: 4-neighbour, both
    // heights within stepHeight of each other.  Node placement keys on
    // these rather than on height, because height alone cannot tell two
    // strips of the same terrace apart when a fence stands between them --
    // both back gardens either side of a 1.81 m boundary sit at -0.80, and
    // clustered by height they read as one floor, whichever strip sat
    // nearer a lattice centre stole the other's node, and the gateway hop
    // chain dead-ended on a garden with nothing to land in.
    const comp = new Int32Array(field.height.length).fill(-1);
    {
      const { nx, nz, height } = field;
      let next = 0;
      const stack = [];
      for (let k0 = 0; k0 < seen.length; k0++) {
        if (!seen[k0] || comp[k0] >= 0) continue;
        comp[k0] = next;
        stack.push(k0);
        while (stack.length) {
          const cur = stack.pop();
          const i = (cur / nz) | 0, j = cur % nz;
          const h = height[cur];
          for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
            const ni = i + di, nj = j + dj;
            if (ni < 0 || nj < 0 || ni >= nx || nj >= nz) continue;
            const k = ni * nz + nj;
            if (!seen[k] || comp[k] >= 0) continue;
            if (Math.abs(height[k] - h) > MOVE.stepHeight + 1e-6) continue;
            comp[k] = next;
            stack.push(k);
          }
        }
        next++;
      }
    }

    const nav = new Nav({ field, seen, comp, grid, spacing,
                          minX: field.minX, minZ: field.minZ,
                          _wide: new Uint8Array(field.height.length) });

    nav._placeNodes(anchors);
    nav._connect();
    nav._prune();
    nav.buildMs = Math.round(performance.now() - t0);
    nav._standable = standable;
    nav._reached = reached;
    return nav;
  }

  // ---- graph construction ------------------------------------------------

  /**
   * Nodes: per `spacing` lattice cell, one per connected FLOOR passing
   * through it (the `comp` labels from build) -- each floor contributing
   * its cell nearest the lattice centre.
   *
   * Per-floor rather than one per cell because hop chains need their
   * landings on the graph: the designed route over the parked bins is
   * ground -> recycle box (0.42) -> bin lid (0.69) -> down into the garden,
   * three hops with two landings, and a graph whose only node thereabouts
   * is the lane floor can never route it -- each edge carries at most one
   * hop (see _lineKind), so every intermediate perch must be a node.  The
   * same rule is what puts a relay on the gateway recycle stack and makes
   * the shut-gate boundary crossable where the level intends it to be.
   *
   * Nearest-reachable-per-floor, never the lattice point itself: a lattice
   * point that lands inside a wall would cost its 2 m cell the node, and a
   * narrow run -- the bin lane is ~0.27 m wide and metres long -- could
   * miss every lattice point and fall off the graph entirely.
   *
   * A floor's node prefers a cell that is genuinely WIDE (see the
   * narrow-top rule above _lineKind's constants) and falls back to a
   * narrow cell only when the floor has nothing wider -- a fence cap, the
   * chain-link's top rail.  Those narrow nodes are kept rather than
   * refused, flagged `narrow: true`, because they are what a hop chain
   * lands on ("the cat hops onto the top rail and steps down" is
   * fences.js's own description of the rear crossing).  The walking rules
   * already guarantee no walk edge can touch them, so a narrow node can
   * relay hops and do nothing else -- which is the whole of what a cat
   * uses a fence top for when it is commuting rather than showing off.
   *
   * Plus a node forced at each anchor (the happy places, every level
   * marker), snapped to its nearest reachable cell within spacing/2 --
   * beyond that the lattice already supplies a node at this density and a
   * wandering snap would only vouch for a spot it does not represent.
   */
  _placeNodes(anchors) {
    const f = this.field, L = this.spacing;
    this.lnx = Math.ceil((f.nx * f.step) / L);
    this.lnz = Math.ceil((f.nz * f.step) / L);
    // Bucket every reachable cell by lattice cell and floor, carrying the
    // distance to the lattice centre.
    const cellsOf = new Map();      // "lattice:floor" -> [[d2, fineIdx]...]
    for (let i = 0; i < f.nx; i++) {
      const x = f.wx(i);
      const a = Math.min(this.lnx - 1, Math.floor((x - f.minX) / L));
      const cx = f.minX + (a + 0.5) * L;
      for (let j = 0; j < f.nz; j++) {
        const k = f.idx(i, j);
        if (!this.seen[k]) continue;
        const z = f.wz(j);
        const bIdx = Math.min(this.lnz - 1, Math.floor((z - f.minZ) / L));
        const cz = f.minZ + (bIdx + 0.5) * L;
        const d2 = (x - cx) * (x - cx) + (z - cz) * (z - cz);
        const lk = (a * this.lnz + bIdx) * 100000 + this.comp[k];
        let bucket = cellsOf.get(lk);
        if (!bucket) cellsOf.set(lk, (bucket = []));
        bucket.push([d2, k]);
      }
    }

    this.nodes = [];
    this.cellNode = new Map();          // fine cell -> node index
    this.latNodes = new Map();          // lattice cell -> [node indices]
    const addNode = (k) => {
      if (this.cellNode.has(k)) return this.cellNode.get(k);
      const i = (k / f.nz) | 0, j = k % f.nz;
      const n = { x: f.wx(i), y: f.height[k], z: f.wz(j), i, j,
                  narrow: !this._wideTop(k) };
      const id = this.nodes.length;
      this.nodes.push(n);
      this.cellNode.set(k, id);
      const lk = Math.floor((n.x - f.minX) / L) * this.lnz +
                 Math.floor((n.z - f.minZ) / L);
      let bucket = this.latNodes.get(lk);
      if (!bucket) this.latNodes.set(lk, (bucket = []));
      bucket.push(id);
      return id;
    };

    for (const bucket of cellsOf.values()) {
      // The most INTERIOR wide cell, nearest the lattice centre among
      // equals; a floor that is all cap, rail or post top keeps a narrow
      // relay node at its d2-nearest cell.  Interior on purpose: a node on
      // a surface's rim is a waypoint the steering aims at from below,
      // which parks the cat flush against the supporting face -- live cats
      // hopping for the bin lid's north EDGE rose into the lid's own
      // overhanging lip and wedged there, while the same hop aimed at the
      // lid's middle clears it.
      bucket.sort((p, q) => p[0] - q[0]);
      let bestK = -1, bestScore = -1;
      for (const [, k] of bucket) {
        if (!this._wideTop(k)) continue;
        const score = this._interior(k);
        if (score > bestScore) { bestScore = score; bestK = k; }
      }
      addNode(bestK >= 0 ? bestK : bucket[0][1]);
    }

    // The spawn is where every AI path starts, so it gets a node of its own
    // rather than trusting the lattice to have put one within snapping
    // range -- and _prune below needs to know which node it is.
    for (const p of [SPAWN, ...anchors]) {
      const k = this._snapReachable(p.x, p.z, L / 2);
      if (k >= 0) {
        const id = addNode(k);
        if (p === SPAWN) this.spawnNode = id;
      }
    }
  }

  /** Nearest reachable fine cell to (x, z) within maxDist metres, or -1. */
  _snapReachable(x, z, maxDist) {
    const f = this.field;
    const ci = f.ci(x), cj = f.cj(z);
    const r = Math.ceil(maxDist / f.step);
    let bk = -1, bd = Infinity;
    for (let di = -r; di <= r; di++) {
      for (let dj = -r; dj <= r; dj++) {
        const i = ci + di, j = cj + dj;
        if (!f.inside(i, j)) continue;
        const k = f.idx(i, j);
        if (!this.seen[k]) continue;
        const d = Math.hypot(f.wx(i) - x, f.wz(j) - z);
        if (d > maxDist || d >= bd) continue;
        bd = d; bk = k;
      }
    }
    return bk;
  }

  /**
   * Is the surface under this cell provably wider than ASSIST_TOP (0.20 m)
   * in both plan axes?  NARROW_SPAN contiguous columns within stepHeight of
   * the cell's own height prove an extent over 0.2 m; see the narrow-top
   * comment by the constants.  The stepHeight tolerance is what keeps real
   * floors wide: a stair tread measures with its neighbouring treads, a
   * sloping lawn with itself.  Cached, because the line tests ask this for
   * every walk step they take.
   *
   * The diagonal clause closes a leak the axis spans alone have: where the
   * side fence's 0.16 m cap crosses the shut gate's 0.12 m top they form a
   * T, the cap supplies the z-span, the gate top (0.16 m lower, inside the
   * tolerance) supplies the x-span, and the junction of two rails measured
   * as floor -- which handed the router a "wide" perch on the fence and
   * un-priced the very landing the narrow rules exist to price.  A real
   * floor patch also has support on a diagonal; a crossing of rails has
   * none.
   */
  _wideTop(k) {
    let v = this._wide[k];
    if (v) return v === 1;
    const f = this.field;
    const i = (k / f.nz) | 0, j = k % f.nz;
    const h = f.height[k];
    let diag = false;
    if (!Number.isNaN(h)) {
      for (let di = -1; di <= 1 && !diag; di += 2) {
        for (let dj = -1; dj <= 1 && !diag; dj += 2) {
          if (!f.inside(i + di, j + dj)) continue;
          const qh = f.height[f.idx(i + di, j + dj)];
          diag = Math.abs(qh - h) <= MOVE.stepHeight;   // NaN compares false
        }
      }
    }
    const wide = diag &&
                 this._span(i, j, h, 1, 0) >= NARROW_SPAN &&
                 this._span(i, j, h, 0, 1) >= NARROW_SPAN;
    this._wide[k] = wide ? 1 : 2;
    return wide;
  }

  /**
   * How far (in cells, capped) the floor extends in the WORST of the four
   * directions -- the distance to the nearest edge, not the total width,
   * which is the difference between a surface's centre and its corner.  A
   * total-width score rated the recycle box's corner as interior as its
   * middle (four neighbours on one side count the same as two on each),
   * and the corner is exactly where a waypoint wedges a cat: standing
   * half-off the box, pressed into the bin beside it, jumping at a lid
   * edge it cannot clear.
   */
  _interior(k, cap = 3) {
    const f = this.field;
    const i = (k / f.nz) | 0, j = k % f.nz;
    const h = f.height[k];
    let worst = cap;
    for (const [di, dj] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
      let n = 0;
      for (let s = 1; s <= cap; s++) {
        const qi = i + di * s, qj = j + dj * s;
        if (!f.inside(qi, qj)) break;
        const qh = f.height[f.idx(qi, qj)];
        if (Number.isNaN(qh) || Math.abs(qh - h) > MOVE.stepHeight) break;
        n++;
      }
      if (n < worst) worst = n;
    }
    return worst;
  }

  /** Contiguous columns through (i, j) along (di, dj) within stepHeight. */
  _span(i, j, h, di, dj, cap = NARROW_SPAN) {
    const f = this.field;
    let n = 1;
    for (let sgn = -1; sgn <= 1; sgn += 2) {
      for (let s = 1; n < cap; s++) {
        const qi = i + sgn * di * s, qj = j + sgn * dj * s;
        if (!f.inside(qi, qj)) break;
        const qh = f.height[f.idx(qi, qj)];
        if (Number.isNaN(qh) || Math.abs(qh - h) > MOVE.stepHeight) break;
        n++;
      }
    }
    return n;
  }

  /**
   * Classify the straight line from (i0,j0) to (i1,j1): "walk", a hop
   * record { hop, ti, tj, rise } naming the jump's take-off cell, or null.
   * Sampled cell by cell exactly as blockedLine samples a jump.
   *
   * A clean walk is a walk.  Otherwise the line gets to spend exactly ONE
   * jump on its first break in the walking rules -- take-off and landing
   * chosen so the jump fits the flood's budget for its distance and the
   * rest of the line still walks -- and a line that would need a second is
   * refused.  That is the "one hop per edge" rule the per-plateau node
   * placement leans on: a chain of jumps is a chain of edges, with a node
   * at every landing.
   *
   * Direction matters twice over: the drop rule is wider than the rise rule
   * (down the recycle box is a walk, back up it is a hop), and a hop's
   * budget is spent from its take-off side.
   */
  _lineKind(i0, j0, i1, j1) {
    const f = this.field, H = f.height;
    const steps = Math.max(Math.abs(i1 - i0), Math.abs(j1 - j0));
    if (!steps) {
      return Number.isNaN(H[f.idx(i0, j0)]) ? null : "walk";
    }
    // One pass to sample the line; everything below reads these.
    const hs = new Float64Array(steps + 1);
    const is = new Int32Array(steps + 1);
    const js = new Int32Array(steps + 1);
    for (let s = 0; s <= steps; s++) {
      const t = s / steps;
      is[s] = Math.round(i0 + (i1 - i0) * t);
      js[s] = Math.round(j0 + (j1 - j0) * t);
      hs[s] = H[f.idx(is[s], js[s])];
    }
    if (Number.isNaN(hs[0])) return null;
    // The whole line may not climb more than one jump, whatever route the
    // rules below would find through it.  WALK_RISE is a per-cell rule --
    // spec.js is explicit that terrain is walkable at any gradient -- but a
    // STACK of collider tops each under stepHeight satisfies it too, and a
    // stack is not terrain: the parked car reads as sill 0.58, bonnet 0.92,
    // windscreen in 0.07-0.13 lips, roof 1.44, which this scan once passed
    // as "one hop and a walk" and the server's first live cats then stood
    // under a 1.77 m fence cap pressing Space forever.  The steering's
    // promise is one Space press per hop waypoint, and one press buys at
    // most jumpHeight of net height; refusing the line here is what makes
    // that promise the graph's promise too.  nav.mjs audits the built graph
    // for exactly this invariant and fails the deploy gate on it.
    //
    // A cumulative cap on chained lip-steps was measured and rejected: with
    // this net cap in place, 41 of 34667 edges still chain per-cell rises
    // past twice stepHeight, and sampling them finds only the level's real
    // staircases -- the deck's dog-leg (six 0.22 m risers), the houses'
    // back steps, the kerb crossed at a slant -- all of which the game's
    // per-lip step-up climbs exactly as the grid says.  A chain cap tight
    // enough to catch a phantom staircase severs the real ones first.
    if (hs[steps] - hs[0] > MOVE.jumpHeight + 1e-6) return null;
    // A walk step must clear the height rules AND keep off narrow tops --
    // the narrow-top rule above: fence caps and rails are landed on, never
    // strolled along.  Checking narrowness after the height test keeps the
    // width cache off NaN cells the heights already refuse.
    const walks = (s) => {
      const dh = hs[s + 1] - hs[s];              // NaN fails both comparisons
      return dh <= WALK_RISE && dh >= -WALK_DROP &&
             this._wideTop(f.idx(is[s], js[s])) &&
             this._wideTop(f.idx(is[s + 1], js[s + 1]));
    };
    let brk = -1;
    for (let s = 0; s < steps; s++) {
      if (!walks(s)) { brk = s + 1; break; }
    }
    if (brk < 0) return "walk";

    // Close the break as the line's one jump.  Two freedoms matter here,
    // and both were bugs before they were freedoms.  The take-off may back
    // up along the already-walked prefix: walking down into a gap is legal
    // (the drop is under WALK_DROP) even when the far side then needs a
    // jump the gap floor cannot make -- the recycle-box -> bin-lid crossing
    // is exactly this shape -- and a cat takes that from the box's edge,
    // not the pit.  And the landing must be one the REST of the line can
    // walk from, not merely the first spot the arc could end: beside the
    // bins the first legal landing is the lane floor a metre down, which
    // spends the jump descending and then stands stuck under the very wall
    // the hop was for.
    const suffix = new Uint8Array(steps + 1);    // rest of the line walks?
    suffix[steps] = 1;
    for (let s = steps - 1; s >= brk; s--) suffix[s] = suffix[s + 1] && walks(s);
    const perStep = Math.hypot((i1 - i0) * f.step, (j1 - j0) * f.step) / steps;
    for (let a = brk - 1; a >= 0; a--) {
      if ((brk - a) * perStep > JUMP_REACH) break;   // nearest landing out of reach
      // Head-room at the take-off: the launch is always full power, so the
      // cat's whole body has to fit under whatever hangs over this cell
      // for at least the rise it needs.  Measured before it was written:
      // hopping to the bin lid from under an overhang, the launch left at
      // full speed and one tick later vy read -0.52 -- the head (0.32 m up)
      // had struck the underside 0.60 m above the box and the jump topped
      // out at 0.25 m of a needed 0.65; the identical jump one cell into
      // the open reached 0.93.  standHeight's CLEARANCE proves 0.34 m of
      // air; a jump needs CAT_HEIGHT plus the rise, and an edge that asks
      // for it under an eave is a cat pressing Space for ever.
      const room = this._clearanceAbove(f.wx(is[a]), f.wz(js[a]), hs[a])
                   - CAT_HEIGHT;
      for (let e = brk; e <= steps; e++) {
        const d = (e - a) * perStep;
        if (d > JUMP_REACH) break;
        if (Number.isNaN(hs[e]) || !suffix[e]) continue;
        const rise = hs[e] - hs[a];
        const budget = d <= f.step * 1.5
          ? MOVE.stepHeight + 1e-6
          : MOVE.jumpHeight * (1 - 0.45 * (d / JUMP_REACH));
        if (rise > Math.min(budget, HOP_RISE, room) || rise < -HOP_DROP) continue;
        if (this._hopBlocked(is[a], js[a], is[e], js[e], hs[a], hs[e])) continue;
        return { hop: true, ti: is[a], tj: js[a], rise };
      }
    }
    return null;
  }

  /**
   * Open air above height h in the column at (x, z): the gap up to the
   * lowest collider underside strictly above h.  standHeight already
   * proved CLEARANCE (0.34 m) of it wherever h is standable; the hop
   * take-off check needs to know how much more there is.
   */
  _clearanceAbove(x, z, h) {
    let clear = Infinity;
    for (const c of this.grid.nearPoint(x, z)) {
      if (x < c.min.x || x > c.max.x || z < c.min.z || z > c.max.z) continue;
      if (c.min.y > h + 1e-3 && c.min.y - h < clear) clear = c.min.y - h;
    }
    return clear;
  }

  /** The walking-only line test -- what string-pull shortcuts must pass. */
  _walkLine(i0, j0, i1, j1) {
    return this._lineKind(i0, j0, i1, j1) === "walk";
  }

  /**
   * blockedLine, plus: a NaN column blocks a hop.
   *
   * The flood's own blocked test only refuses columns whose HEIGHT pokes
   * above the arc, which is blind exactly where it matters most: a house
   * wall's top is above MAX_STAND, so its columns sample as NaN, and by the
   * flood's rule a hop would sail straight through two storeys of brick.
   * (First observed as a route that entered the hero house 0.3 m to the
   * side of its own front door.)  A NaN column under a hop therefore
   * refuses the hop.  This does NOT refuse jumping the gap between a bin
   * and a crate -- the lane floor a metre below still gives those columns a
   * height -- it refuses only columns nothing can stand in at any level,
   * which on this map means solid geometry and sub-CLEARANCE crevices.
   */
  _hopBlocked(i, j, ni, nj, h, nh) {
    const f = this.field;
    const steps = Math.max(Math.abs(ni - i), Math.abs(nj - j));
    if (steps < 2) return false;
    const ceiling = Math.max(h, nh) + 0.25;
    for (let s = 1; s < steps; s++) {
      const t = s / steps;
      const mi = Math.round(i + (ni - i) * t);
      const mj = Math.round(j + (nj - j) * t);
      const mh = f.height[f.idx(mi, mj)];
      if (Number.isNaN(mh) || mh > ceiling) return true;
    }
    return false;
  }

  /**
   * Directed edges between every node pair within EDGE_REACH lattice cells
   * whose straight line resolves to a walk or a single hop.  Cost is the 3D
   * distance -- hops pay HOP_COST, and NARROW_LAND on top for a narrow
   * landing -- and the heuristic below is bare 3D distance, so a penalty
   * can only ever make A* cautious, never wrong.
   */
  _connect() {
    const f = this.field;
    this.adj = this.nodes.map(() => []);
    let edges = 0, hops = 0;
    for (let u = 0; u < this.nodes.length; u++) {
      const nu = this.nodes[u];
      const la = Math.floor((nu.x - f.minX) / this.spacing);
      const lb = Math.floor((nu.z - f.minZ) / this.spacing);
      for (let da = -EDGE_REACH; da <= EDGE_REACH; da++) {
        for (let db = -EDGE_REACH; db <= EDGE_REACH; db++) {
          // Bounds-checked: a lattice key is a * lnz + b, and a negative row
          // aliases into the end of the row before it -- at the map corners
          // that hands back a bucket from the far side of the level.
          const a = la + da, bIdx = lb + db;
          if (a < 0 || bIdx < 0 || a >= this.lnx || bIdx >= this.lnz) continue;
          const bucket = this.latNodes.get(a * this.lnz + bIdx);
          if (!bucket) continue;
          for (const v of bucket) {
            if (v === u) continue;
            const nv = this.nodes[v];
            const kind = this._lineKind(nu.i, nu.j, nv.i, nv.j);
            if (!kind) continue;
            const d = Math.hypot(nv.x - nu.x, nv.y - nu.y, nv.z - nu.z);
            const hop = kind !== "walk";
            const cost = hop ? d * HOP_COST * (nv.narrow ? NARROW_LAND : 1) : d;
            // Hop edges carry their take-off cell and rise so the deploy
            // gate can re-check the head-room against the raw colliders.
            this.adj[u].push(hop ? [v, cost, true, kind] : [v, cost, false]);
            edges++;
            if (hop) hops++;
          }
        }
      }
    }
    this._edges = edges;
    this._hopEdges = hops;
  }

  /**
   * Keep only the nodes that can get BACK to the spawn's node, by a
   * reverse flood over the directed adjacency.
   *
   * The movement rules compose into sinks nobody designed: a top whose
   * walk edges the narrow rule stripped and whose upward hop the head-room
   * rule refused keeps its incoming edges and loses every way out.  A cat
   * hops on, the graph goes silent, and the brain blacklists goal after
   * goal from a perch it cannot leave -- the soak run found two cats
   * parked on the bin staircase with `goal: none` for minutes.  Rather
   * than reason about which pair of rules produces the next sink, prune
   * anything that cannot return: a node the graph cannot route OUT of is
   * a trap by definition, whatever made it one.  (A real cat could still
   * walk off and fall; wherever that fall is executable the graph already
   * carries it as a drop hop, so what this removes is only the perches
   * even falling does not serve.)  nav.mjs re-checks the survivors as its
   * fourth audit.
   */
  _prune() {
    if (this.spawnNode == null) return;
    const n = this.nodes.length;
    const rev = this.nodes.map(() => []);
    for (let u = 0; u < n; u++) for (const [v] of this.adj[u]) rev[v].push(u);
    const keep = new Uint8Array(n);
    const q = [this.spawnNode];
    keep[this.spawnNode] = 1;
    while (q.length) {
      const u = q.pop();
      for (const w of rev[u]) if (!keep[w]) { keep[w] = 1; q.push(w); }
    }
    const remap = new Int32Array(n).fill(-1);
    const nodes = [];
    for (let id = 0; id < n; id++) {
      if (keep[id]) { remap[id] = nodes.length; nodes.push(this.nodes[id]); }
    }
    this._pruned = n - nodes.length;
    if (!this._pruned) return;

    const adj = [];
    let edges = 0, hops = 0;
    for (let id = 0; id < n; id++) {
      if (!keep[id]) continue;
      const out = [];
      for (const e of this.adj[id]) {
        if (remap[e[0]] < 0) continue;         // an edge into a trap goes too
        e[0] = remap[e[0]];
        out.push(e);
        edges++;
        if (e[2]) hops++;
      }
      adj.push(out);
    }
    this.nodes = nodes;
    this.adj = adj;
    this.spawnNode = remap[this.spawnNode];
    this._edges = edges;
    this._hopEdges = hops;

    const f = this.field;
    this.cellNode = new Map();
    this.latNodes = new Map();
    nodes.forEach((nd, id) => {
      this.cellNode.set(f.idx(nd.i, nd.j), id);
      const lk = Math.floor((nd.x - f.minX) / this.spacing) * this.lnz +
                 Math.floor((nd.z - f.minZ) / this.spacing);
      let bucket = this.latNodes.get(lk);
      if (!bucket) this.latNodes.set(lk, (bucket = []));
      bucket.push(id);
    });
  }

  // ---- queries -----------------------------------------------------------

  /** Standing height at (x, z) off the fine grid, metres, or null. */
  standHeight(x, z) {
    const f = this.field;
    const i = f.ci(x), j = f.cj(z);
    if (!f.inside(i, j)) return null;
    const h = f.height[f.idx(i, j)];
    return Number.isNaN(h) ? null : h;
  }

  /** Is (x, z) in the flood reached from the spawn? */
  reachable(x, z) {
    const f = this.field;
    const i = f.ci(x), j = f.cj(z);
    return f.inside(i, j) ? this.seen[f.idx(i, j)] === 1 : false;
  }

  /**
   * Nearest node to (x, z) in plan, within maxDist metres, or -1.  Plan
   * distance and nothing else -- a caller standing under the deck will get
   * the deck node if it is closest; path() applies the line tests that
   * actually disambiguate.
   */
  nearestNode(x, z, maxDist = SNAP_DIST) {
    return this._nearest(x, z, maxDist, null)[0];
  }

  /**
   * Nearest node, optionally requiring a legal line between the node and
   * the point -- `dir` is "from" (point leads to node: a path start) or
   * "to" (node leads to point: a path goal).  Nearest by 3D distance when
   * the caller supplies a y, so a point ON the deck snaps to the deck node
   * and not to the patio node in the same column.  Returns [id, kind].
   */
  /**
   * The fine cell to line-test an endpoint from: the column under the
   * point, unless the caller's y says the cat is standing somewhere that
   * column cannot see.  A capsule 0.38 m long can bridge two rims -- live
   * cats stood with the bin lid under their forepaws, the recycle stack
   * under the hind, and their CENTRE over the 0.2 m slot between, whose
   * floor reads 1.13 m below the paws.  Every line test from that cell
   * fails, path() returns null for every goal, and the brain blacklists
   * its whole world and goes idle.  When the column disagrees with the
   * caller's y by more than a step, the seat moves to the nearest cell
   * (within the capsule's 0.19 m reach, padded a cell) that matches it.
   */
  _seatCell(x, z, y) {
    const f = this.field;
    let ci = f.ci(x), cj = f.cj(z);
    if (y == null) return [ci, cj];
    const h0 = f.inside(ci, cj) ? f.height[f.idx(ci, cj)] : NaN;
    if (!Number.isNaN(h0) && Math.abs(h0 - y) <= MOVE.stepHeight) return [ci, cj];
    let bd = Infinity;
    for (let di = -3; di <= 3; di++) {
      for (let dj = -3; dj <= 3; dj++) {
        const qi = f.ci(x) + di, qj = f.cj(z) + dj;
        if (!f.inside(qi, qj)) continue;
        const qh = f.height[f.idx(qi, qj)];
        if (Number.isNaN(qh) || Math.abs(qh - y) > MOVE.stepHeight) continue;
        const dd = Math.abs(di) + Math.abs(dj);
        if (dd < bd) { bd = dd; ci = qi; cj = qj; }
      }
    }
    return [ci, cj];
  }

  _nearest(x, z, maxDist, dir, y = null) {
    const f = this.field;
    const [ci, cj] = this._seatCell(x, z, y);
    const cand = [];
    const la0 = Math.floor((x - f.minX) / this.spacing);
    const lb0 = Math.floor((z - f.minZ) / this.spacing);
    const R = Math.ceil(maxDist / this.spacing) + 1;
    for (let da = -R; da <= R; da++) {
      for (let db = -R; db <= R; db++) {
        const a = la0 + da, b = lb0 + db;
        if (a < 0 || b < 0 || a >= this.lnx || b >= this.lnz) continue;
        const bucket = this.latNodes.get(a * this.lnz + b);
        if (!bucket) continue;
        for (const id of bucket) {
          const n = this.nodes[id];
          const plan = Math.hypot(n.x - x, n.z - z);
          if (plan > maxDist) continue;
          const d = y === null ? plan : Math.hypot(n.x - x, n.y - y, n.z - z);
          cand.push([d, id]);
        }
      }
    }
    cand.sort((a, b) => a[0] - b[0]);
    for (const [, id] of cand) {
      const n = this.nodes[id];
      if (!dir) return [id, null];
      const kind = dir === "from"
        ? this._lineKind(ci, cj, n.i, n.j)
        : this._lineKind(n.i, n.j, ci, cj);
      if (kind) return [id, kind === "walk" ? "walk" : "hop"];
    }
    return [-1, null];
  }

  /**
   * A route the steering can execute: A* over the waypoint graph, then
   * string-pulled.  Returns [{x, y, z, jump}, ...] world waypoints ending at
   * `to` itself, or null if either end cannot be joined to the graph or no
   * route connects them.  `jump` on a waypoint means the leg INTO it
   * contains a hop -- the steering should be ready to press Space on that
   * leg; every other leg is plain held keys.
   *
   * The string-pull is not a nicety.  Raw A* output is a staircase of 2 m
   * lattice nodes, and a cat that visits each one walks like a Roomba.
   * Dropping every intermediate waypoint whose neighbours can see each
   * other straightens that into the two or three legs a cat would take --
   * but only across runs of WALK edges, with the shortcut itself passing
   * the walking-line test.  Hop waypoints are pinned: a hop's take-off and
   * landing are exactly where the budget said they were, and collapsing a
   * hop into a longer straight line would hand the cat a leap the budget
   * never approved.
   */
  path(from, to) {
    const f = this.field;
    const [s, sKind] = this._nearest(from.x, from.z, SNAP_DIST, "from", from.y ?? null);
    const [g, gKind] = this._nearest(to.x, to.z, SNAP_DIST, "to", to.y ?? null);
    if (s < 0 || g < 0) return null;
    const chain = this._astar(s, g);
    if (!chain) return null;

    // Assemble from -> nodes -> to, with the kind of each connecting leg.
    // The joins at both ends were proved by _nearest above; their kinds ride
    // on the start node and on `to`.  `to` becomes the last waypoint if the
    // cat can stand there; a goal that is only NEAR the graph (its own cell
    // a wall, say) ends at the goal node instead, and the caller can see
    // that from the coordinates.
    const [fi, fj] = this._seatCell(from.x, from.z, from.y ?? null);
    const pts = [{ x: from.x, y: from.y ?? this.standHeight(from.x, from.z),
                   z: from.z, i: fi, j: fj, into: null }];
    chain[0].into = sKind;
    for (const link of chain) {
      const n = this.nodes[link.id];
      pts.push({ x: n.x, y: n.y, z: n.z, i: n.i, j: n.j, into: link.into });
    }
    const ti = f.ci(to.x), tj = f.cj(to.z);
    const th = f.inside(ti, tj) ? f.height[f.idx(ti, tj)] : NaN;
    if (!Number.isNaN(th) && this.seen[f.idx(ti, tj)]) {
      pts.push({ x: to.x, y: th, z: to.z, i: ti, j: tj, into: gKind });
    }

    // Pull each run of walk legs; pin every hop landing and take-off.
    const out = [];
    let a = 0;
    while (a < pts.length - 1) {
      let b = a + 1;
      if (pts[b].into !== "hop") {
        // Furthest point reachable across walk legs only, then back off
        // until the straight line actually walks.
        let limit = a + 1;
        while (limit < pts.length - 1 && pts[limit + 1].into !== "hop") limit++;
        b = limit;
        while (b > a + 1 && !this._walkLine(pts[a].i, pts[a].j, pts[b].i, pts[b].j)) b--;
      }
      out.push({ x: pts[b].x, y: pts[b].y, z: pts[b].z,
                 jump: pts[b].into === "hop" });
      a = b;
    }
    return out;
  }

  /**
   * Plain A*, Euclidean 3D heuristic, over the directed adjacency.  Returns
   * the chain as [{id, into}] where `into` is the kind of the edge INTO
   * that node, or null for the start.
   */
  _astar(s, g) {
    const n = this.nodes.length;
    const gs = new Float64Array(n).fill(Infinity);
    const prev = new Int32Array(n).fill(-1);
    const prevHop = new Uint8Array(n);
    const done = new Uint8Array(n);
    const goal = this.nodes[g];
    const h = (id) => {
      const nd = this.nodes[id];
      return Math.hypot(nd.x - goal.x, nd.y - goal.y, nd.z - goal.z);
    };
    // Binary heap of [f, id].  Stale entries are skipped via `done`, which
    // is cheaper than decrease-key at a couple of thousand nodes.
    const heap = [[h(s), s]];
    gs[s] = 0;
    while (heap.length) {
      const top = heap[0];
      const last = heap.pop();
      if (heap.length) { heap[0] = last; _sink(heap, 0); }
      const u = top[1];
      if (done[u]) continue;
      if (u === g) {
        const chain = [];
        for (let c = g; c >= 0; c = prev[c]) {
          chain.push({ id: c, into: prev[c] < 0 ? null
                                    : prevHop[c] ? "hop" : "walk" });
        }
        return chain.reverse();
      }
      done[u] = 1;
      for (const [v, w, hop] of this.adj[u]) {
        if (done[v]) continue;
        const cand = gs[u] + w;
        if (cand < gs[v]) {
          gs[v] = cand;
          prev[v] = u;
          prevHop[v] = hop ? 1 : 0;
          heap.push([cand + h(v), v]);
          _swim(heap, heap.length - 1);
        }
      }
    }
    return null;
  }

  get stats() {
    const f = this.field;
    return {
      cells: f.nx * f.nz,
      standable: this._standable,
      reached: this._reached,
      nodes: this.nodes.length,
      edges: this._edges,
      hopEdges: this._hopEdges,
      pruned: this._pruned ?? 0,
      buildMs: this.buildMs,
    };
  }
}

function _swim(h, i) {
  while (i > 0) {
    const p = (i - 1) >> 1;
    if (h[p][0] <= h[i][0]) break;
    const t = h[p]; h[p] = h[i]; h[i] = t;
    i = p;
  }
}
function _sink(h, i) {
  for (;;) {
    let m = i;
    const l = 2 * i + 1, r = 2 * i + 2;
    if (l < h.length && h[l][0] < h[m][0]) m = l;
    if (r < h.length && h[r][0] < h[m][0]) m = r;
    if (m === i) break;
    const t = h[m]; h[m] = h[i]; h[i] = t;
    i = m;
  }
}

// ---------------------------------------------------------------------------

/**
 * The boot gate: every `solo` happy place the nav cannot actually serve,
 * with a reason.  Empty means boot; anything else means refuse -- a happy
 * place the brain cannot reach is a want that never empties, and the visible
 * symptom is a cat that mopes for no reason, which no log line will ever be
 * traced back from.
 *
 * Four checks, each subsuming none of the others: the approach point must be
 * standable at all, must be in the reachable flood, must have a graph node
 * within SNAP_DIST, and -- the one that actually bites -- path() from the
 * spawn must succeed, which is where a perch with no landing node or a
 * climb past the hop budget finally fails.
 */
export function checkPlaces(nav, places) {
  const bad = [];
  for (const place of places) {
    if (!place.solo) continue;
    const a = approachPoint(place);
    const at = `(${a.x.toFixed(1)}, ${a.z.toFixed(1)})`;
    const h = nav.standHeight(a.x, a.z);
    if (h === null) {
      bad.push({ id: place.id, why: `approach ${at} has nothing to stand on` });
      continue;
    }
    if (!nav.reachable(a.x, a.z)) {
      bad.push({ id: place.id,
                 why: `approach ${at} (stands at y ${h.toFixed(2)}) is not in ` +
                      `the reachable flood` });
      continue;
    }
    if (nav.nearestNode(a.x, a.z, SNAP_DIST)[0] < 0) {
      bad.push({ id: place.id,
                 why: `no graph node within ${SNAP_DIST} m of approach ${at}` });
      continue;
    }
    const route = nav.path(SPAWN, a);
    if (!route) {
      bad.push({ id: place.id,
                 why: `no route from spawn to approach ${at} -- it stands at ` +
                      `y ${h.toFixed(2)} but no chain of walks and single ` +
                      `hops (rise <= ${MOVE.jumpHeight} m budgeted by reach) ` +
                      `arrives there` });
    }
  }
  return bad;
}
